import { useState } from 'react';
import { toast } from '@/components/ui/use-toast';
import { openAuthModal } from '@/store/slices/auth-modal.slice';
import { buildAuthRedirectUrl, asApiError } from '@/utils';
import { ROUTES } from '@/constants';
import { useJoinLobbyMutation } from '@/store/api';

export function useJoinLobby({
	isCrowdLockStarted,
	navigate,
	eventId,
	user,
	lobbyResponse,
	isMobile,
	dispatch,
	isAdmin,
	isEventClosed,
	lobbyId,
}: any) {
	const [joinLobby] = useJoinLobbyMutation();
	const [showPasswordDialog, setShowPasswordDialog] = useState(false);
	const [password, setPassword] = useState('');
	const [isJoining, setIsJoining] = useState(false);
	const [showSuccessDialog, setShowSuccessDialog] = useState(false);

	const handleJoinLobby = async (providedPassword: any = null, source?: any) => {
		try {
			setIsJoining(true);
			if (isCrowdLockStarted) {
				toast({
					title: 'Lobby already started',
					description: 'This Crowd Lock lobby has already started. You can no longer join it.',
					variant: 'destructive',
				});

				navigate(`${ROUTES.EVENT_DETAILS}?id=${eventId}`);
				return;
			}

			if (!user && lobbyResponse?.is_private) {
				if (isMobile) {
					dispatch(openAuthModal());
				} else {
					navigate(buildAuthRedirectUrl());
				}
			} else {
				const participantsList = lobbyResponse?.lobby_users || [];

				if (
					user &&
					!participantsList.some((userObj: any) => userObj.user_id === user.id) &&
					!isAdmin &&
					!isEventClosed
				) {
					await joinLobby({ id: lobbyId, password: providedPassword, source }).unwrap();
					if (!Object.isFrozen(participantsList)) {
						participantsList.push({ isHide: false, user_id: user.id });
					}
				}
				setShowPasswordDialog(false);
				setPassword('');
				setShowSuccessDialog(true);
			}
		} catch (error: unknown) {
			const e = asApiError(error);
			if (e.response?.status === 400 && e.response?.data?.message === 'Incorrect password') {
				toast({
					title: 'Incorrect Password',
					description: 'Please try again with the correct password.',
					variant: 'destructive',
				});
			} else {
				toast({
					title: 'Error',
					description: 'Failed to join the lobby. Please try again.',
					variant: 'destructive',
				});
			}
			console.error(error);
			throw new Error('Error joining lobby:');
		} finally {
			setIsJoining(false);
		}
	};

	return {
		showPasswordDialog,
		setShowPasswordDialog,
		password,
		setPassword,
		isJoining,
		showSuccessDialog,
		setShowSuccessDialog,
		handleJoinLobby,
	};
}
